import { Logo } from '@/components/Header';
import React, { useMemo, useState } from 'react';
import {
  Link,
  useNavigate,
} from 'react-router-dom';

import { Heart, Sparkles, Users, GitBranch, ShieldCheck, ArrowRight, KeyRound, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  QuickTrialCTA,
  EmotionalHookSection,
  ViralHookSection,
  StickyMobileTrialCTA,
  TrustMiniStrip,
} from '@/components/invite/ConversionEnhancements';


import { useMembership } from '@/contexts/MembershipContext';
import {
  clearInviteContext,
} from '@/lib/inviteContext';
import {
  parseSecurePublicInviteReference,
} from '@/lib/publicInviteAuthority';



/**
 * Public /join entry point — additive, does NOT replace any existing flow.
 *
 * - Anyone (logged in or not) can land here directly or via a shared link.
 * - "Start My Family Tree" → opens the signup AuthModal (clean tree, user
 *   is auto-centered as "You" by the existing FamilyGraph bootstrap).
 * - "Join an Existing Family" → reveals an inline input where the user
 *   can paste an invite LINK (https://www.kinscircleapp.com/invite/<id>) or
 *   an invite CODE (the bare user_id). We resolve it to an inviter
 *   profile, persist it as kc:pendingInvite (same key used by
 *   InviteLanding + AppLayout), and then prompt signup. After auth,
 *   AppLayout will pop the RelationshipOnboardingModal automatically.
 */
const Join: React.FC = () => {
  const navigate = useNavigate();

  const {
    openAuthModal,
    openMembershipModal,
  } = useMembership();



  const [showJoinExisting, setShowJoinExisting] = useState(false);
  const [code, setCode] = useState('');
  const [resolving, setResolving] =
    useState(false);

  const [resolveError, setResolveError] =
    useState<string | null>(null);
  // /join is intentionally PUBLIC and works for everyone — including
  // signed-in users — so the page can be linked, bookmarked, or shared
  // freely without any auto-redirect that would trap users away from
  // the public onboarding surface.


  const handleStartFresh = () => {
    // Clear any stale pending invite — this user is starting a brand-new
    // family (independent tree, they become the center). Wipes BOTH
    // localStorage and sessionStorage so the lost-invite fallback can't
    // mistakenly re-bind them later.
    clearInviteContext();
    openAuthModal('signup');
  };

  const handleResolveCode = (
    e?: React.FormEvent,
  ) => {
    e?.preventDefault();
    setResolveError(null);

    const reference =
      parseSecurePublicInviteReference(
        code,
      );

    if (!reference) {
      setResolveError(
        'Paste the complete secure invitation link, including its token. Ask the sender to create a new invitation if the link is incomplete.',
      );
      return;
    }

    setResolving(true);
    navigate(reference.canonicalPath);
  };


  const features = useMemo(
    () => [
      { icon: <GitBranch className="w-5 h-5 text-fuchsia-600" />, title: 'Auto-connected network', body: 'Add a parent, partner, sibling or in-law and the rest of your family network links itself.' },
      { icon: <Users className="w-5 h-5 text-pink-600" />, title: 'Every household, one circle', body: "Both sides, blended homes, step-family — together in one private space." },
      { icon: <ShieldCheck className="w-5 h-5 text-emerald-600" />, title: 'Private family vault', body: 'Photos, videos and stories visible only to verified family members.' },
    ],
    [],
  );


  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50">
      {/* Header */}
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white shadow group-hover:scale-105 transition">
            <img
              src="/brand/kinscircle-tree-icon.png"
              alt="KinsCircle"
              className="w-full h-full rounded-full object-cover"
            />
          </div>
          <div className="font-serif text-xl font-bold text-slate-900">
            Kins<span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">Circle</span>
          </div>
        </Link>
        <button
          onClick={() => openAuthModal('signup')}
          className="text-sm font-semibold text-fuchsia-700 hover:text-fuchsia-900 hover:underline underline-offset-4"
        >
          Sign up
        </button>
      </header>

      {/* Hero */}
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-14">

        <div className="mb-8 flex justify-center">
          <img
            src="/brand/kinscircle-tree-icon.png"
            alt="KinsCircle family legacy platform"
            className="w-full max-w-[360px] sm:max-w-[460px] h-auto object-contain rounded-3xl shadow-xl border border-fuchsia-100 bg-white"
          />
        </div>


      <section className="mt-12 mb-16 sm:mb-24 max-w-5xl mx-auto">
        <div className="rounded-3xl bg-gradient-to-br from-white via-fuchsia-50/40 to-amber-50/50 border border-fuchsia-100 shadow-lg p-8 sm:p-10 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-fuchsia-50 text-fuchsia-700 font-semibold text-sm mb-6">
            Why KinsCircle Exists
          </div>

          <h2 className="font-serif text-3xl sm:text-5xl font-bold text-slate-900 mb-6">
            Bringing Families Back Together
          </h2>

          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            Social media connects us to everyone else. KinsCircle reconnects us to family.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            Families have grown apart across cities, states, and generations. Stories fade,
            family history gets lost, and too many children grow up not knowing the people
            who came before them.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            KinsCircle gives families one private place to preserve memories, share news,
            plan reunions, celebrate milestones, protect the family tree, and bring every
            generation back closer.
          </p>

          <div className="grid grid-cols-5 gap-2 mb-6 max-w-xs mx-auto">
            <div className="h-2 rounded-full bg-pink-400"></div>
            <div className="h-2 rounded-full bg-amber-400"></div>
            <div className="h-2 rounded-full bg-emerald-400"></div>
            <div className="h-2 rounded-full bg-sky-400"></div>
            <div className="h-2 rounded-full bg-fuchsia-500"></div>
          </div>

          <div className="rounded-2xl bg-white/80 border border-amber-100 p-6 mb-6 shadow-sm">
            <p className="text-xl font-bold text-slate-900">No outside influence.</p>
            <p className="text-xl font-bold text-slate-900">No distractions.</p>
            <p className="text-xl font-bold text-slate-900">No strangers.</p>
            <p className="text-3xl font-serif font-bold mt-3 bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-500 bg-clip-text text-transparent">
              Just Family.
            </p>
          </div>

          <p className="font-serif text-3xl sm:text-4xl font-bold bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-500 bg-clip-text text-transparent">
            Not just a family tree.<br />A living family network.
          </p>
        </div>
      </section>

        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-fuchsia-200 text-fuchsia-700 text-[11px] font-bold uppercase tracking-widest mb-5 shadow-sm">
            <Sparkles className="w-3.5 h-3.5" /> 3-Day Free Trial · Card Required · Cancel Anytime
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 leading-[1.05]">
            Join My Family on KinsCircle
            <br />
            <span className="bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-500 bg-clip-text text-transparent">
              for Modern Families
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
            A private family operating system for everyone you love — partners,
            in-laws, step-family, blended households, every generation.
          </p>
          <p className="mt-3 font-serif text-base sm:text-lg italic text-slate-500 max-w-xl mx-auto">
            Preserve your family story. Stay connected, together.
          </p>
          <div className="mt-5">
            <TrustMiniStrip />
          </div>
        </div>



        {/* ─────────────── Quick trial CTA (above the fold) ─────────────── */}
        <div className="mt-8">
          <QuickTrialCTA
            onCta={() => openMembershipModal()}
            label="Start Free Trial"
          />
        </div>


        {/* Simple visual preview of a family tree — pure inline SVG so it
            ships with no extra requests and renders crisply at every size.
            Three generations: grandparents → parents → you + sibling. */}
        <div className="mt-8 rounded-3xl bg-white/70 backdrop-blur border border-fuchsia-100 p-4 sm:p-6 shadow-sm">
          <svg
            viewBox="0 0 360 180"
            className="w-full h-auto"
            role="img"
            aria-label="Family tree preview with three generations"
          >
            {/* Connectors */}
            <g stroke="#f0abfc" strokeWidth="2" fill="none" strokeLinecap="round">
              {/* grandparents → parents */}
              <path d="M70 40 L130 90" />
              <path d="M130 40 L130 90" />
              <path d="M230 40 L230 90" />
              <path d="M290 40 L230 90" />
              {/* parents joiner */}
              <path d="M130 90 L230 90" />
              {/* parents → kids */}
              <path d="M180 90 L120 150" />
              <path d="M180 90 L240 150" />
            </g>

            {/* Generation 1 — grandparents */}
            <g>
              <circle cx="70" cy="40" r="18" fill="#fce7f3" stroke="#f472b6" strokeWidth="2" />
              <text x="70" y="44" textAnchor="middle" fontSize="11" fill="#9d174d" fontWeight="700">G‑Ma</text>
              <circle cx="130" cy="40" r="18" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
              <text x="130" y="44" textAnchor="middle" fontSize="11" fill="#1e3a8a" fontWeight="700">G‑Pa</text>
              <circle cx="230" cy="40" r="18" fill="#fce7f3" stroke="#f472b6" strokeWidth="2" />
              <text x="230" y="44" textAnchor="middle" fontSize="11" fill="#9d174d" fontWeight="700">G‑Ma</text>
              <circle cx="290" cy="40" r="18" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
              <text x="290" y="44" textAnchor="middle" fontSize="11" fill="#1e3a8a" fontWeight="700">G‑Pa</text>
            </g>

            {/* Generation 2 — parents */}
            <g>
              <circle cx="130" cy="100" r="20" fill="#fbcfe8" stroke="#ec4899" strokeWidth="2" />
              <text x="130" y="104" textAnchor="middle" fontSize="11" fill="#831843" fontWeight="700">Mom</text>
              <circle cx="230" cy="100" r="20" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="2" />
              <text x="230" y="104" textAnchor="middle" fontSize="11" fill="#1e3a8a" fontWeight="700">Dad</text>
            </g>

            {/* Generation 3 — you (highlighted) + sibling */}
            <g>
              <circle cx="120" cy="155" r="18" fill="#fde68a" stroke="#f59e0b" strokeWidth="2" />
              <text x="120" y="159" textAnchor="middle" fontSize="10" fill="#78350f" fontWeight="700">Sib</text>
              <circle cx="240" cy="155" r="22" fill="url(#youGrad)" stroke="#a21caf" strokeWidth="2.5" />
              <text x="240" y="159" textAnchor="middle" fontSize="11" fill="#fff" fontWeight="800">You</text>
            </g>

            <defs>
              <linearGradient id="youGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#c026d3" />
                <stop offset="50%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>
          </svg>
          <p className="text-center text-[12px] text-slate-500 mt-2">
            Your family graph grows automatically as relatives join.
          </p>
        </div>


        {/* CTA card */}
        <div className="mt-10 rounded-3xl bg-white shadow-xl border border-fuchsia-100 overflow-hidden">
          <div className="p-6 sm:p-8 space-y-4">
            <Button
              onClick={handleStartFresh}
              className="w-full h-14 text-base bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:opacity-95 text-white font-semibold gap-2 shadow rounded-2xl"
            >
              <Sparkles className="w-5 h-5" />
              Start My Family Tree
              <ArrowRight className="w-5 h-5 ml-1" />
            </Button>

            {/* Risk reversal + trust line directly under the primary CTA */}
            <div className="text-center space-y-1">
              <p className="text-xs font-semibold text-slate-700">
                Cancel in 2 taps. No questions asked.
              </p>
              <p className="text-[11px] text-slate-500 inline-flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Private. Secure. Family-only access.
              </p>
            </div>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
                <span className="bg-white px-2 text-slate-400">or</span>
              </div>
            </div>

            {!showJoinExisting && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowJoinExisting(true)}
                className="w-full h-12 rounded-2xl border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 hover:text-fuchsia-800 font-semibold"
              >
                <Users className="w-4 h-4 mr-2" />
                Join a Family
              </Button>
            )}


            {showJoinExisting && (
              <form onSubmit={handleResolveCode} className="space-y-3 rounded-2xl bg-fuchsia-50/60 border border-fuchsia-100 p-4">
                <div>
                  <label htmlFor="invite-code" className="text-xs font-bold uppercase tracking-wider text-fuchsia-700 flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5" /> Paste your invite link or code
                  </label>
                  <Input
                    id="invite-code"
                    autoFocus
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      setResolveError(null);
                    }}
                    placeholder="https://kinscircleapp.com/invite/…?t=…"
                    className="mt-1.5 bg-white"
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    Paste the complete secure link from the Invite Family menu. Invite codes and bare account IDs are not accepted.
                  </p>
                </div>

                {resolveError && (
                  <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-lg p-2.5">
                    {resolveError}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={
                    resolving ||
                    !code.trim()
                  }
                  className="w-full rounded-xl bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
                >
                  {resolving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Opening secure invitation…
                    </>
                  ) : (
                    <>
                      Verify invitation
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setShowJoinExisting(false);
                    setCode('');
                    setResolveError(null);
                  }}
                  className="w-full text-[11px] text-slate-500 hover:text-slate-700"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Feature pillars */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl bg-white/70 backdrop-blur border border-fuchsia-100 p-4 text-center sm:text-left">
              <div className="inline-flex w-10 h-10 rounded-full bg-white items-center justify-center shadow-sm border border-fuchsia-100 mb-2">
                {f.icon}
              </div>
              <div className="font-bold text-sm text-slate-900">{f.title}</div>
              <div className="text-[12px] text-slate-600 mt-0.5 leading-snug">{f.body}</div>
            </div>
          ))}
        </div>

        {/* Guided onboarding preview */}
        <div className="mt-10 rounded-3xl bg-white border border-fuchsia-100 shadow-sm p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-700 mb-2">What happens next</div>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-5">A guided 3-step start</h2>
          <ol className="space-y-3">
            {[
              { n: 1, t: 'Add the people closest to you', d: 'Parents, a partner, siblings, or kids — any of them anchors your family network.' },
              { n: 2, t: 'Welcome the wider family', d: "In-laws, step-parents, cousins, chosen family — every relationship that matters belongs here." },
              { n: 3, t: 'Invite them to join you', d: 'Each invite expands your living family network and pulls their branch in too.' },
            ].map((s) => (
              <li key={s.n} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-fuchsia-600 to-pink-500 text-white text-sm font-bold flex items-center justify-center shadow">
                  {s.n}
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{s.t}</div>
                  <div className="text-xs text-slate-600">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>



        {/* ─────────────── Emotional hook ─────────────── */}
        <div className="mt-10">
          <EmotionalHookSection />
        </div>

        {/* ─────────────── Viral hook ─────────────── */}
        <div className="mt-10">
          <ViralHookSection />
        </div>

        {/* Conversion-focused experience showcase — features, free trial,
            trust block, social proof. Reuses the shared component so the
            /invite/:id and /join surfaces stay in sync. */}
        <div className="mt-12">
        </div>


        <p className="mt-8 text-center text-[11px] text-slate-500">
          By continuing you agree to our{' '}
          <Link to="/terms" className="underline hover:text-slate-700">Terms</Link> and{' '}
          <Link to="/privacy" className="underline hover:text-slate-700">Privacy</Link>.
        </p>
      </main>

      {/* ─────────────── Sticky mobile bottom CTA ─────────────── */}
      <StickyMobileTrialCTA
        onCta={() => openMembershipModal()}
        label="Start Free Trial"
      />
    </div>
  );
};

export default Join;

